/**
 * Secure logging utility that prevents sensitive information exposure in production
 */

import seqLogger from './seqLogger';

const isDevelopment = process.env.NODE_ENV === 'development';
const isTest = process.env.NODE_ENV === 'test';

// Create a logger that only logs in development/test environments
const createLogger = () => {
  if (isDevelopment || isTest) {
    return {
      log: (...args) => {
        // Filter out excessive repetitive logs
        const message = args.join(' ');
        
        // Send to SEQ if enabled
        if (seqLogger.isEnabled()) {
          seqLogger.info(message, 'Logger', { args: args.length });
        }
        
        if (message.includes('appTitle translation') || 
            message.includes('Current language:') ||
            message.includes('Record claims:') ||
            message.includes('Outreach status for record:') ||
            message.includes('Complete outreach_status object:') ||
            message.includes('No outreach status for record:') ||
            message.includes('Found phone numbers:') ||
            message.includes('DataGrid received data:') ||
            (message.includes('Found') && message.includes('records with outreach')) ||
            message.includes('Making diagnostic API request') ||
            message.includes('Diagnostic check for first record') ||
            message.includes('Actual data received in frontend') ||
            message.includes('Difference between API and frontend') ||
            message.includes('Contact info available for auto-fill') ||
            message.includes('Extracted URL from contactInfo') ||
            message.includes('Loading outreaches with viewMode') ||
            (message.includes('Loaded') && message.includes('outreach records')) ||
            message.includes('Language set to:') ||
            message.includes('Database data is already loaded') ||
            message.includes('Data is loaded but user is not logged in') ||
            message.includes('User is logged in according to server') ||
            message.includes('User logged in and data is loaded') ||
            message.includes('Fetching data with params:') ||
            (message.includes('Fetched data with') && message.includes('records')) ||
            message.includes('Total records from backend:') ||
            (message.includes('Subscription') && message.includes('response:')) ||
            message.includes('OutreachStats:') ||
            message.includes('DisclaimerDialog:') ||
            message.includes('Processing URL for SalesOutreach') ||
            message.includes('Extracted company name:') ||
            message.includes('Checking if domain exists') ||
            message.includes('Making DNS check request') ||
            message.includes('DNS check result structure') ||
            message.includes('Domain exists according to') ||
            (message.includes('Found') && message.includes('existing records for URL')) ||
            message.includes('URL search activity logged') ||
            message.includes('Found website in field') ||
            message.includes('checkDNS') ||
            message.includes('check DNS called at') ||
            message.includes('Invalid domain:') ||
            message.includes('checkDNS - ') ||
            message.includes('DNS data updated in state') ||
            message.includes('DNS check timestamp') ||
            message.includes('DNS data was served from cache') ||
            message.includes('Loading security changes') ||
            message.includes('Security changes loaded') ||
            (message.includes('Found') && message.includes('IPs needing lookup')) ||
            message.includes('unique IPs') ||
            (message.includes('Processing') && message.includes('IPs')) ||
            message.includes('After deduplication') ||
            message.includes('Total IPs to process') ||
            message.includes('Looking up IP') ||
            message.includes('Performing reverse DNS lookup') ||
            message.includes('Found hostname for') ||
            message.includes('Completed batch of') ||
            // Additional patterns from the console output
            (message.includes('Fetched') && message.includes('records, total:')) ||
            message.includes('[AuthContext] Auto-modal check:') ||
            message.includes('API: Getting sales outreach statistics') ||
            message.includes('API: Getting today and upcoming meetings') ||
            message.includes('API: Get today events response:') ||
            message.includes('API: Get user outreach statistics response:') ||
            message.includes('Added backward compatibility alias') ||
            // New patterns from latest console output
            message.startsWith('📤 API:') ||
            message.startsWith('API:') ||
            // CompanyResearch component verbose logging patterns
            message.includes('🚀 CompanyResearch component loaded with debug filtering') ||
            message.includes('Extracted domain:') ||
            message.includes('Checking DNS for domain:') ||
            message.includes('Using backend URL:') ||
            message.includes('Fetching research data for company:') ||
            message.includes('Record data:') ||
            message.includes('Sending to API:') ||
            message.includes('Checking for existing enriched contacts for record:') ||
            message.includes('DEBUG: Initializing URL search section for record') ||
            message.includes('Setting URL fields and search section visibility to:') ||
            message.includes('DEBUG: New showUrlFields state:') ||
            message.includes('DEBUG: New showUrlSearchSection state:') ||
            (message.includes('Found') && message.includes('existing enriched contacts')) ||
            message.includes('Searching Lusha using:') ||
            message.includes('Fetching Lusha contacts with request data:') ||
            message.includes('Lusha search response:') ||
            message.includes('Full response data structure:') ||
            message.includes('Data field keys:') ||
            (message.includes('Received') && message.includes('contacts from Lusha API')) ||
            message.includes('First contact example:') ||
            message.includes('Contact fields:') ||
            message.includes('Processed contacts for display:') ||
            message.includes('🔍 Frontend deduplication:') ||
            message.includes('Merged contacts with enriched data:') ||
            message.includes('📋 Contacts loaded for record') ||
            message.includes('Full research API response:') ||
            message.includes('Found research data in expected results format') ||
            message.includes('Received research data with length:') ||
            message.includes('Found sources section in markdown:') ||
            (message.includes('Parsed') && message.includes('sections from markdown')) ||
            (message.includes('Found') && message.includes('sources in API response:')) ||
            message.includes('Citation [') ||
            message.includes('- Expanded panel:') ||
            message.includes('- Sources available:') ||
            message.includes('- Source found:') ||
            message.includes('- Source ID:') ||
            message.includes('- Source URL:') ||
            message.includes('- Source title:') ||
            message.includes('🔧 Using request ID:') ||
            message.includes('🔍 Enriching contact ID:') ||
            message.includes('🔧 Request ID source:') ||
            message.includes('🎯 Using Lusha contact ID:') ||
            message.includes('Full enrich response structure:') ||
            message.includes('🔄 Updating contact') ||
            message.includes('Enriched data to apply:') ||
            message.includes('✅ Found contact to update:') ||
            message.includes('🎯 Seniority update for') ||
            message.includes('Original:') ||
            message.includes('New:') ||
            message.includes('✅ Updated contact:') ||
            message.includes('🔄 Updated contact list for record') ||
            (message.includes('✅ Contact') && message.includes('updated successfully with enrichment data')) ||
            message.includes('📊 Updated contacts count:') ||
            message.includes('🔄 Mapped Lusha seniority') ||
            // Additional SalesOutreach and URL checking logs
            message.includes('Checking if URL exists in records:') ||
            message.includes('checkDNS') ||
            message.includes('✅ API: Domain security changes retrieved successfully') ||
            message.includes('ForceUpdate triggered') ||
            message.includes('refreshing IP lookup data') ||
            // Contact merging debug logs
            message.includes('Contact cleanup performed:') ||
            message.includes('=== MERGING CONTACTS DEBUG ===') ||
            message.includes('Enriched contacts:') ||
            message.includes('Search contacts:') ||
            message.includes('Keeping search contact') ||
            message.includes('Filtering out search contact') ||
            message.includes('- no match found') ||
            message.includes('- matched by ID') ||
            message.includes('Merged result:') ||
            message.includes('enriched +') ||
            message.includes('new =') ||
            message.includes('total') ||
            message.includes('Final merged contacts:') ||
            message.includes('=== END MERGING DEBUG ===') ||
            // Additional repetitive logs to suppress
            message.includes('Meeting info:') ||
            message.includes('Followup info:') ||
            message.includes('Rendering icon:') ||
            message.includes('No outreach status for record:') ||
            message.includes('Rendering meeting icon with date:') ||
            // Outreach and filter logs
            message.includes('Outreach status changed:') ||
            message.includes('Adding method filters:') ||
            message.includes('Final outreach filters:') ||
            message.includes('Outreach filters applied:') ||
            (message.includes('Sample of') && message.includes('records from filtered results:')) ||
            (message.includes('Record ') && message.includes(': {recordId:')) ||
            message.includes('Triggering Lusha contacts fetch for recordId:') ||
            // Lusha contact logs
            (message.includes('Found') && message.includes('Lusha contacts')) ||
            message.includes('Processed Lusha contacts:') ||
            // DNS logs
            message.includes('Using local timestamp for DNS check:') ||
            message.includes('DNS data was freshly checked, not from cache') ||
            message.includes('🔍 API: Getting security changes for domain:') ||
            // checkDNS logs from SalesOutreach
            message === 'checkDNS') {
          // Skip these verbose logs even in development
          return;
        }
        console.log(...args);
      },
      error: (...args) => {
        const message = args.join(' ');
        if (seqLogger.isEnabled()) {
          seqLogger.error(message, 'Logger', { args: args.length });
        }
        console.error(...args);
      },
      warn: (...args) => {
        const message = args.join(' ');
        if (seqLogger.isEnabled()) {
          seqLogger.warn(message, 'Logger', { args: args.length });
        }
        console.warn(...args);
      },
      info: (...args) => {
        const message = args.join(' ');
        if (seqLogger.isEnabled()) {
          seqLogger.info(message, 'Logger', { args: args.length });
        }
        console.info(...args);
      },
      debug: (...args) => {
        // Debug logs are even more restricted - only show important ones
        const message = args.join(' ');
        if (seqLogger.isEnabled()) {
          seqLogger.debug(message, 'Logger', { args: args.length });
        }
        if (message.includes('DIRECT TRANSLATION CHECK') || 
            message.includes('Environment variables debug')) {
          console.debug(...args);
        }
      },
      // For API debugging - only in development and less verbose
      apiRequest: (method, url) => {
        // Send to SEQ if enabled
        if (seqLogger.isEnabled() && !url.includes('/api/logs/frontend')) {
          seqLogger.info(`API Request: ${method.toUpperCase()} ${url}`, 'API', { method, url });
        }
        // Only log API requests for critical endpoints (very restrictive)
        if (!url.includes('/api/logs/frontend') && 
            !url.includes('/api/subscription/status') &&
            !url.includes('/check-disclaimer') &&
            !url.includes('/api/public/data-status') &&
            !url.includes('/check_login') &&
            !url.includes('/search') &&
            !url.includes('/api/subscription/plans') &&
            !url.includes('/api/sales-outreach/statistics') &&
            !url.includes('/api/sales-outreach/today-events') &&
            !url.includes('/api/user/guidance-status') &&
            !url.includes('/api/outreach/status') &&
            !url.includes('/api/check-url-in-records') &&
            !url.includes('/api/log-url-search') &&
            !url.includes('/api/sales-outreach?record_id=') &&
            !url.includes('/api/dns-changes/domain/') &&
            !url.includes('/api/dns/reverse') &&
            !url.includes('/api/pai-research') &&
            !url.includes('/api/lusha/enriched-contacts') &&
            !url.includes('/api/lusha/enrich')) {
          console.log(`📤 API: ${method.toUpperCase()} ${url}`);
        }
      },
      apiResponse: (status, url) => {
        // Send to SEQ if enabled
        if (seqLogger.isEnabled() && !url.includes('/api/logs/frontend')) {
          seqLogger.info(`API Response: ${status} ${url}`, 'API', { status, url });
        }
        // Only log API responses for critical endpoints (very restrictive)
        if (!url.includes('/api/logs/frontend') && 
            !url.includes('/api/subscription/status') &&
            !url.includes('/api/public/data-status') &&
            !url.includes('/check_login') &&
            !url.includes('/search') &&
            !url.includes('/api/subscription/plans') &&
            !url.includes('/api/sales-outreach/statistics') &&
            !url.includes('/api/sales-outreach/today-events') &&
            !url.includes('/api/user/guidance-status') &&
            !url.includes('/api/outreach/status') &&
            !url.includes('/api/check-url-in-records') &&
            !url.includes('/api/log-url-search') &&
            !url.includes('/api/sales-outreach?record_id=') &&
            !url.includes('/api/dns-changes/domain/') &&
            !url.includes('/api/dns/reverse') &&
            !url.includes('/api/pai-research') &&
            !url.includes('/api/lusha/enriched-contacts') &&
            !url.includes('/api/lusha/enrich')) {
          console.log(`📥 API: ${status} ${url}`);
        }
      },
      apiError: (error, context) => {
        const errorMessage = error?.message || String(error);
        if (seqLogger.isEnabled()) {
          seqLogger.error(`API Error [${context}]: ${errorMessage}`, 'API', { 
            context, 
            error: errorMessage,
            stack: error?.stack 
          });
        }
        console.error(`❌ API Error [${context}]:`, error);
      }
    };
  } else {
    // In production, create no-op functions
    const noop = () => {};
    return {
      log: noop,
      error: noop,
      warn: noop,
      info: noop,
      debug: noop,
      apiRequest: noop,
      apiResponse: noop,
      apiError: noop
    };
  }
};

const logger = createLogger();

// Override global console methods in production to prevent accidental logging
if (!isDevelopment && !isTest) {
  console.log = () => {};
  console.warn = () => {};
  console.error = () => {};
  console.info = () => {};
  console.debug = () => {};
}

export default logger;
